import { useState, useRef, useEffect } from 'react'
import { Link, NavLink, useNavigate } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { formatName, resolveImg } from '@/lib/helpers'
import Avatar from '@/components/ui/Avatar'
import { Menu, X, User, UserCircle, Shield, Crown, Bookmark, LogOut } from 'lucide-react'

const navLinks = [
  { label: 'Нүүр', to: '/' },
  { label: 'Сургалт', to: '/courses' },
  { label: 'Видео', to: '/videos' },
  { label: 'Нийтлэл', to: '/feed' },
  { label: 'Багш нар', to: '/teachers' },
]

const staticDropdownItems = [
  { label: 'Бүртгэл', icon: User, to: '/settings' },
  { label: 'Аюулгүй байдал', icon: Shield, to: '/settings/security' },
  { label: 'Premium', icon: Crown, to: '/settings/premium' },
  { label: 'Хадгалсан', icon: Bookmark, to: '/saved' },
]

export default function Header() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false)
      }
    }
    if (dropdownOpen) document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [dropdownOpen])

  const profilePath =
    user?.role === 'teacher' && user?.teacherId
      ? `/teachers/${user.teacherId}`
      : `/profile/${user?._id || 'me'}`

  const dropdownItems = [
    { label: 'Миний профайл', icon: UserCircle, to: profilePath },
    ...staticDropdownItems,
  ]

  const avatarSrc = resolveImg(user?.avatar)

  const handleLogout = () => {
    logout()
    setDropdownOpen(false)
    setMobileOpen(false)
    navigate('/')
  }

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-[1024px] mx-auto flex items-center justify-between h-16 px-4">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <img src="/logo.png" alt="ARYA" className="h-9" />
        </Link>

        {/* Navigation - Desktop */}
        <nav className="hidden md:flex gap-1">
          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === '/'}
              className={({ isActive }) =>
                isActive
                  ? 'bg-primary-500 text-white rounded-full px-4 py-1.5 text-sm font-medium'
                  : 'text-gray-600 px-4 py-1.5 text-sm font-medium hover:text-gray-900'
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        {/* Auth / User section - Desktop */}
        <div className="hidden md:block">
          {user ? (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2.5 cursor-pointer hover:opacity-80 transition"
              >
                <Avatar src={avatarSrc} alt={formatName(user.fullName)} size="sm" />
                <span className="text-sm font-medium text-gray-800 max-w-[120px] truncate">
                  {formatName(user.fullName)}
                </span>
              </button>

              {/* Dropdown */}
              {dropdownOpen && (
                <div className="absolute right-0 top-full mt-2 w-52 bg-white border border-gray-200 rounded-xl shadow-lg py-1.5 z-50">
                  {dropdownItems.map((item) => (
                    <button
                      key={item.to}
                      onClick={() => { navigate(item.to); setDropdownOpen(false) }}
                      className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
                    >
                      <item.icon className="size-4 text-gray-400" />
                      {item.label}
                    </button>
                  ))}
                  <hr className="my-1.5 border-gray-100" />
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 transition"
                  >
                    <LogOut className="size-4" />
                    Гарах
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-4">
              <Link
                to="/login"
                className="text-sm font-medium text-gray-600 hover:text-gray-900 px-3 py-2"
              >
                Нэвтрэх
              </Link>
              <Link
                to="/register"
                className="bg-primary-500 text-white rounded-full px-4 py-2 text-sm font-semibold shadow-xs hover:bg-primary-600 transition"
              >
                Бүртгүүлэх
              </Link>
            </div>
          )}
        </div>

        {/* Mobile menu button */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="md:hidden p-2 text-gray-600"
        >
          {mobileOpen ? <X className="size-6" /> : <Menu className="size-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-gray-200 bg-white px-4 pb-4">
          <nav className="flex flex-col gap-1 py-2">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === '/'}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  isActive
                    ? 'bg-primary-500 text-white rounded-xl px-4 py-2.5 text-sm font-medium'
                    : 'text-gray-600 px-4 py-2.5 text-sm font-medium hover:bg-gray-50 rounded-xl'
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>
          <div className="border-t border-gray-100 pt-3">
            {user ? (
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2.5 px-4 py-2">
                  <Avatar src={avatarSrc} alt={formatName(user.fullName)} size="sm" />
                  <span className="text-sm font-medium text-gray-800">{formatName(user.fullName)}</span>
                </div>
                {dropdownItems.map((item) => (
                  <button
                    key={item.to}
                    onClick={() => { navigate(item.to); setMobileOpen(false) }}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-xl transition"
                  >
                    <item.icon className="size-4 text-gray-400" />
                    {item.label}
                  </button>
                ))}
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-xl transition"
                >
                  <LogOut className="size-4" />
                  Гарах
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-3 px-4">
                <Link to="/login" onClick={() => setMobileOpen(false)} className="text-sm font-medium text-gray-600">
                  Нэвтрэх
                </Link>
                <Link
                  to="/register"
                  onClick={() => setMobileOpen(false)}
                  className="bg-primary-500 text-white rounded-full px-4 py-2 text-sm font-semibold"
                >
                  Бүртгүүлэх
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  )
}
